import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../api/query-keys.js'
import {
  Button,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  selectTriggerVariants,
} from '@speaking-coach/ui'
import { Listbox, ListboxMessage, ListboxOption, ListboxSearch, useListboxId } from '../components/ui/listbox.js'
import { adminContentClient } from './api.js'
import { CATALOG_SEARCH_PLACEHOLDERS } from './catalog-types.js'
import type { CatalogListParams, CatalogRef } from './catalog-types.js'
import type { CatalogPage } from './catalog-navigation.js'

export function CatalogPicker({ page, label, selected, filters, onChange }: {
  page: CatalogPage; label: string; selected: CatalogRef | null; filters: CatalogListParams; onChange: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [search, setSearch] = useState('')
  const [active, setActive] = useState('all')
  const listId = useListboxId()
  useEffect(() => { const timer = setTimeout(() => setSearch(input.trim()), 250); return () => clearTimeout(timer) }, [input])
  const params = { ...filters, q: search, limit: 20 }
  const result = useQuery({ queryKey: queryKeys.catalogList(page, params), queryFn: () => adminContentClient.list(page, params), enabled: open })
  const rows = result.data?.items ?? []
  function choose(id: string) { onChange(id === 'all' ? '' : id); setOpen(false); setInput(''); setSearch(''); setActive('all') }
  return <Popover open={open} onOpenChange={setOpen}>
    <div className="relative flex w-full items-center">
    <PopoverTrigger role="combobox" aria-label={label} className={`${selectTriggerVariants({ size: 'sm' })} ${selected ? 'pr-14' : 'pr-9'}`} onKeyDown={event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true) } }}>
      <span className="truncate">{selected?.label ?? `All ${page}`}</span>
    </PopoverTrigger>
    {selected ? <Button type="button" variant="ghost" size="icon" aria-label={`Clear ${label.toLowerCase()} filter`} title={`Clear ${label.toLowerCase()} filter`} onClick={() => choose('all')} className="absolute right-7 size-6 text-text-secondary hover:text-text-primary"><Icon name="clear" size={14} /></Button> : null}
    <Icon name="choose" className="pointer-events-none absolute right-3 opacity-60" />
    </div>
    <PopoverContent className="w-(--anchor-width) p-0">
      <ListboxSearch listId={listId} label={`Search ${label.toLowerCase()}`} placeholder={CATALOG_SEARCH_PLACEHOLDERS[page]} value={input} onValueChange={setInput} activeId={active} optionIds={['all', ...rows.map(row => row.id)]} onActiveIdChange={setActive} onSelect={choose} onDismiss={() => setOpen(false)} />
      <Listbox id={listId} label={label}>
        <ListboxOption listId={listId} value="all" active={active === 'all'} onHover={() => setActive('all')} onSelect={() => choose('all')}>All {page}</ListboxOption>
        {result.isPending ? <ListboxMessage>Searching…</ListboxMessage> : null}
        {result.isError ? <ListboxMessage>Could not load content. <Button size="sm" variant="secondary" onClick={() => void result.refetch()}>Try again</Button></ListboxMessage> : null}
        {rows.map(row => <ListboxOption key={row.id} listId={listId} value={row.id} active={active === row.id} onHover={() => setActive(row.id)} onSelect={() => choose(row.id)}>{row.label}{!filters.program && row.program && page !== 'programs' ? ` · ${row.program.label}` : ''}{!filters.level && row.level && page === 'themes' ? ` · ${row.level.label}` : ''}</ListboxOption>)}
        {result.isSuccess && !rows.length ? <ListboxMessage>No matching content.</ListboxMessage> : null}
        {result.data && result.data.total > rows.length ? <ListboxMessage>Showing the first 20 matches. Keep typing to narrow the search.</ListboxMessage> : null}
      </Listbox>
    </PopoverContent>
  </Popover>
}
